/**
 * Entrenamiento en curso (Req. 11, 12).
 *
 * - Cada cambio se guarda al momento, así que al cerrar y abrir la app el
 *   entrenamiento sigue donde se quedó, con el descanso correcto (Req. 11.7,
 *   12.5).
 * - Solo hay uno en curso a la vez (Req. 11.8).
 * - Marcar una serie y empezar su descanso van en la misma transacción.
 * - Las cargas se guardan como se escribieron (kg, lb o placa) y en kg cuando
 *   se puede convertir; la vez pasada se muestra en la unidad capturada.
 * - Cada entrenamiento guarda su modalidad (gimnasio o casa) al empezar, y la
 *   vez pasada de un ejercicio sale de la misma modalidad (0.3.4).
 * - Historial (0.3.7, Req. 13): un terminado se corrige con las mismas
 *   funciones; sus series nuevas quedan hechas y un ejercicio sin series se
 *   quita, como al terminar. Su fecha se cambia con todas sus horas.
 */

import { db, leerSettings } from './schema';
import { nuevoId } from './ids';
import { enEscritura } from './tx';
import type { Exercise, Modalidad, RoutineExercise, Workout, WorkoutExercise, WorkoutSet, UnidadCarga } from './types';
import { ahoraIso, diferenciaDias, esFechaLocal, type FechaLocal } from '@/domain/dates';
import { fechaLocalDe, moverDias } from '@/domain/time';
import { capturarCarga, type CargaCapturada } from '@/domain/units';
import { ErrorValidacion } from '@/domain/errors';
import { limpiarPausaYVariante, modalidadDe, type PausaYVariante } from '@/domain/modalidad';
import {
  admitePorLado,
  agruparPorBiserie,
  ajustarDescanso,
  anotadaPorLado,
  cargaEnUnidad,
  configuracionDeRutina,
  despuesDeMarcar,
  iniciarDescanso,
  lugarAlAgregar,
  lugarAlRestaurar,
  prellenarSeries,
  resumenEntrenamiento,
  ultimaSesion,
  type DespuesDeMarcar,
  type ResumenEntrenamiento,
  type SesionAnterior,
  type VecinosEnSesion,
} from '@/domain/entrenamiento';

export interface EjercicioEnCurso {
  we: WorkoutExercise;
  ejercicio: Exercise | undefined;
  series: WorkoutSet[];
  /** La vez pasada (sin contar este entrenamiento). */
  anterior: SesionAnterior | null;
  /** En casa: las variantes que ya se usaron con este ejercicio, la más reciente primero. */
  variantes: string[];
}

export interface EntrenamientoDetalle {
  workout: Workout;
  ejercicios: EjercicioEnCurso[];
}

const porOrden = <T extends { orden: number }>(a: T, b: T) => a.orden - b.orden;

/** LECTURA PURA: el que está en curso, si hay. */
export async function leerEnCurso(): Promise<Workout | null> {
  return (await db.workouts.where('estado').equals('en_curso').first()) ?? null;
}

/**
 * LECTURA PURA: los terminados y cuáles tienen al menos una serie, para
 * proponer la sesión que sigue (un entrenamiento vacío no cuenta como hecho).
 */
export async function leerTerminados(): Promise<{ terminados: Workout[]; conSeries: Set<string> }> {
  const [terminados, ids] = await Promise.all([
    db.workouts.where('estado').equals('terminado').toArray(),
    db.workoutSets.orderBy('workoutId').uniqueKeys(),
  ]);
  return { terminados, conSeries: new Set(ids.map(String)) };
}

/**
 * LECTURA PURA: la vez pasada de un ejercicio en esa modalidad: en casa no se
 * prellena con lo del gimnasio, ni al revés. Trae la pausa y la variante de
 * esa vez, si se anotaron.
 */
export async function leerVezPasada(
  exerciseId: string,
  excluirWorkoutId: string | null = null,
  modalidad: Modalidad = 'gimnasio',
): Promise<SesionAnterior | null> {
  const series = await db.workoutSets.where('exerciseId').equals(exerciseId).toArray();
  const ids = [...new Set(series.map((s) => s.workoutId))];
  const workouts = (await db.workouts.bulkGet(ids)).filter(
    (w): w is Workout => w !== undefined && modalidadDe(w) === modalidad,
  );
  const anterior = ultimaSesion(series, new Map(workouts.map((w) => [w.id, w])), excluirWorkoutId);
  const primera = anterior?.series[0];
  if (anterior === null || primera === undefined) return anterior;
  const we = await db.workoutExercises.get(primera.workoutExerciseId);
  return { ...anterior, pausaSeg: we?.pausaSeg ?? null, variante: we?.variante ?? null };
}

/** LECTURA PURA: el entrenamiento con sus ejercicios, series y la vez pasada de cada uno. */
export async function leerEntrenamiento(id: string): Promise<EntrenamientoDetalle | null> {
  const workout = await db.workouts.get(id);
  if (workout === undefined) return null;
  const [items, series] = await Promise.all([
    db.workoutExercises.where('workoutId').equals(id).toArray(),
    db.workoutSets.where('workoutId').equals(id).toArray(),
  ]);
  items.sort(porOrden);
  const ejercicios = await db.exercises.bulkGet(items.map((i) => i.exerciseId));
  const modalidad = modalidadDe(workout);
  const anteriores = await Promise.all(items.map((i) => leerVezPasada(i.exerciseId, id, modalidad)));
  const variantes = modalidad === 'casa' ? await Promise.all(items.map((i) => leerVariantesUsadas(i.exerciseId, id))) : [];
  return {
    workout,
    ejercicios: items.map((we, i) => ({
      we,
      ejercicio: ejercicios[i],
      series: series.filter((s) => s.workoutExerciseId === we.id).sort(porOrden),
      anterior: anteriores[i] ?? null,
      variantes: variantes[i] ?? [],
    })),
  };
}

/** LECTURA PURA: las variantes anotadas con ese ejercicio en casa, la más reciente primero (hasta 6). */
export async function leerVariantesUsadas(exerciseId: string, excluirWorkoutId: string | null = null): Promise<string[]> {
  const wes = (await db.workoutExercises.where('exerciseId').equals(exerciseId).toArray()).filter(
    (we) => we.workoutId !== excluirWorkoutId && typeof we.variante === 'string' && we.variante !== '',
  );
  const workouts = new Map(
    (await db.workouts.bulkGet([...new Set(wes.map((we) => we.workoutId))]))
      .filter((w): w is Workout => w !== undefined && modalidadDe(w) === 'casa')
      .map((w) => [w.id, w]),
  );
  const ordenadas = wes
    .filter((we) => workouts.has(we.workoutId))
    .sort((a, b) => (workouts.get(b.workoutId)?.inicio ?? '').localeCompare(workouts.get(a.workoutId)?.inicio ?? ''));
  return [...new Set(ordenadas.map((we) => we.variante as string))].slice(0, 6);
}

function unidadDe(ejercicio: Exercise | undefined, preferida: UnidadCarga): UnidadCarga {
  return ejercicio?.unidadCaptura ?? preferida;
}

function serieNueva(
  workoutId: string,
  we: WorkoutExercise,
  orden: number,
  borrador: ReturnType<typeof prellenarSeries>[number],
): WorkoutSet {
  const carga = borrador.pesoCapturado;
  return {
    id: nuevoId('ser'),
    workoutId,
    workoutExerciseId: we.id,
    exerciseId: we.exerciseId,
    orden,
    tipoSerie: borrador.tipoSerie,
    pesoKg: carga === null ? null : capturarCarga(carga.valor, carga.unidad).pesoKg,
    pesoCapturado: carga,
    reps: borrador.reps,
    // Por lado (0.3.5): las de cada lado; los demás no llevan estos campos
    ...(we.porLado === true ? { repsIzq: borrador.repsIzq ?? null, repsDer: borrador.repsDer ?? null } : {}),
    duracionSeg: borrador.duracionSeg,
    distanciaM: borrador.distanciaM,
    rir: null,
    hecha: false,
    hechaEn: null,
  };
}

/** La pausa y la variante de hoy: las de la vez pasada en esta modalidad o, sin historial, las de la rutina. */
function pausaYVarianteIniciales(anterior: SesionAnterior | null, plan: PausaYVariante | null): Pick<WorkoutExercise, 'pausaSeg' | 'variante'> {
  if (anterior !== null) return { pausaSeg: anterior.pausaSeg ?? null, variante: anterior.variante ?? null };
  return { pausaSeg: plan?.pausaSeg ?? null, variante: plan?.variante ?? null };
}

/**
 * Empieza un entrenamiento desde una rutina (con las series objetivo y la
 * vez pasada) o vacío (Req. 11.1, 11.2). Si ya hay uno en curso, no crea
 * otro (Req. 11.8). La modalidad es la de la rutina; el libre usa la que se
 * indique o la que muestra Gym. ESCRIBE. Devuelve el id.
 */
export async function iniciarEntrenamiento(
  routineId: string | null,
  ahora: Date = new Date(),
  modalidadLibre?: Modalidad,
): Promise<string> {
  const ajustes = await leerSettings();
  const enCurso = await leerEnCurso();
  if (enCurso !== null) {
    throw new ErrorValidacion('Ya tienes un entrenamiento en curso: termínalo o descártalo antes de empezar otro.');
  }
  const rutina = routineId === null ? undefined : await db.routines.get(routineId);
  if (routineId !== null && rutina === undefined) throw new ErrorValidacion('Esa rutina ya no existe.');
  const modalidad = modalidadDe(rutina ?? { modalidad: modalidadLibre ?? ajustes.modalidadGym });
  const items = routineId === null ? [] : (await db.routineExercises.where('routineId').equals(routineId).toArray()).sort(porOrden);
  const ejercicios = await db.exercises.bulkGet(items.map((i) => i.exerciseId));
  const anteriores = await Promise.all(items.map((i) => leerVezPasada(i.exerciseId, null, modalidad)));

  const id = nuevoId('ent');
  const inicio = ahora.toISOString();
  const workout: Workout = {
    id,
    estado: 'en_curso',
    routineId,
    nombre: rutina?.nombre ?? 'Entrenamiento libre',
    fecha: fechaLocalDe(inicio),
    inicio,
    fin: null,
    notas: null,
    descanso: null,
    modalidad,
    creadoEn: inicio,
    actualizadoEn: inicio,
  };
  const wes: WorkoutExercise[] = [];
  const series: WorkoutSet[] = [];
  items.forEach((item, i) => {
    const we: WorkoutExercise = {
      id: nuevoId('ene'),
      workoutId: id,
      exerciseId: item.exerciseId,
      orden: i,
      // La misma configuración que al volver a agregarlo (0.3.8)
      ...configuracionDeRutina(item),
      ...(modalidad === 'casa' ? pausaYVarianteIniciales(anteriores[i] ?? null, item) : {}),
    };
    wes.push(we);
    const borradores = prellenarSeries(
      item.series,
      anteriores[i] ?? null,
      unidadDe(ejercicios[i], ajustes.unidadPeso),
      item.porLado === true,
    );
    borradores.forEach((b, orden) => series.push(serieNueva(id, we, orden, b)));
  });

  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    // Otra pestaña pudo empezar uno mientras tanto
    if ((await db.workouts.where('estado').equals('en_curso').count()) > 0) {
      throw new ErrorValidacion('Ya tienes un entrenamiento en curso.');
    }
    await db.workouts.add(workout);
    if (wes.length > 0) await db.workoutExercises.bulkAdd(wes);
    if (series.length > 0) await db.workoutSets.bulkAdd(series);
  });
  return id;
}

async function tocar(workoutId: string): Promise<void> {
  await db.workouts.update(workoutId, { actualizadoEn: ahoraIso() });
}

export interface CambiosSerie {
  pesoCapturado?: CargaCapturada | null;
  reps?: number | null;
  /** Por lado (0.3.5). */
  repsIzq?: number | null;
  repsDer?: number | null;
  duracionSeg?: number | null;
  distanciaM?: number | null;
  tipoSerie?: WorkoutSet['tipoSerie'];
  rir?: number | null;
}

function validarCambios(c: CambiosSerie): void {
  if (c.pesoCapturado != null && !(Number.isFinite(c.pesoCapturado.valor) && c.pesoCapturado.valor >= 0 && c.pesoCapturado.valor <= 2000)) {
    throw new ErrorValidacion('Revisa la carga: debe ser un número de 0 a 2,000.');
  }
  for (const r of [c.reps, c.repsIzq, c.repsDer]) {
    if (r != null && !(Number.isInteger(r) && r >= 0 && r <= 1000)) {
      throw new ErrorValidacion('Las repeticiones deben ser un número entero.');
    }
  }
  if (c.rir != null && !(Number.isInteger(c.rir) && c.rir >= 0 && c.rir <= 5)) {
    throw new ErrorValidacion('El RIR va de 0 a 5.');
  }
  for (const v of [c.duracionSeg, c.distanciaM]) {
    if (v != null && !(Number.isFinite(v) && v >= 0)) throw new ErrorValidacion('Revisa el tiempo o la distancia.');
  }
}

/** Cambia lo capturado de una serie, al momento (Req. 11.7). ESCRIBE. */
export async function actualizarSerie(id: string, cambios: CambiosSerie): Promise<void> {
  validarCambios(cambios);
  await enEscritura([db.workoutSets, db.workouts], async () => {
    const serie = await db.workoutSets.get(id);
    if (serie === undefined) return;
    const siguiente: WorkoutSet = { ...serie, ...cambios };
    if ('pesoCapturado' in cambios) {
      const c = cambios.pesoCapturado ?? null;
      siguiente.pesoKg = c === null ? null : capturarCarga(c.valor, c.unidad).pesoKg;
    }
    await db.workoutSets.put(siguiente);
    await tocar(serie.workoutId);
  });
}

/**
 * Pone esa carga en las series del ejercicio que todavía no están hechas
 * (Req. 26.2; 0.8.1). La sugerencia de progresión no cambia nada por su
 * cuenta: esto lo hace la persona con un toque, y lo ya hecho no se toca.
 * Devuelve cuántas series cambiaron. ESCRIBE.
 */
export async function fijarCargaDeLasQueFaltan(workoutExerciseId: string, carga: CargaCapturada): Promise<number> {
  validarCambios({ pesoCapturado: carga });
  return enEscritura([db.workoutSets, db.workouts], async () => {
    const series = (await db.workoutSets.where('workoutExerciseId').equals(workoutExerciseId).toArray()).filter(
      (s) => !s.hecha,
    );
    if (series.length === 0) return 0;
    const pesoKg = capturarCarga(carga.valor, carga.unidad).pesoKg;
    for (const s of series) {
      await db.workoutSets.put({ ...s, pesoKg, pesoCapturado: { valor: carga.valor, unidad: carga.unidad } });
    }
    await tocar(series[0]?.workoutId ?? '');
    return series.length;
  });
}

/**
 * Marca o desmarca una serie con un toque (Req. 11.5). Al marcarla empieza
 * el descanso, en la misma transacción; en una biserie, solo al completar la
 * serie de todos. ESCRIBE. Devuelve qué sigue.
 */
export async function marcarSerie(id: string, hecha: boolean, ahora: Date = new Date()): Promise<DespuesDeMarcar> {
  return enEscritura([db.workoutSets, db.workoutExercises, db.workouts], async () => {
    const serie = await db.workoutSets.get(id);
    if (serie === undefined) return { tipo: 'ninguno' };
    // En un terminado todo está hecho y no hay descanso (0.3.7)
    if ((await db.workouts.get(serie.workoutId))?.estado === 'terminado') return { tipo: 'ninguno' };
    await db.workoutSets.update(id, { hecha, hechaEn: hecha ? ahora.toISOString() : null });
    if (!hecha) {
      await tocar(serie.workoutId);
      return { tipo: 'ninguno' };
    }
    const [ejercicios, series] = await Promise.all([
      db.workoutExercises.where('workoutId').equals(serie.workoutId).toArray(),
      db.workoutSets.where('workoutId').equals(serie.workoutId).toArray(),
    ]);
    const despues = despuesDeMarcar(id, ejercicios, series);
    await db.workouts.update(serie.workoutId, {
      descanso: despues.tipo === 'descanso' && despues.segundos > 0 ? iniciarDescanso(despues.segundos, ahora) : null,
      actualizadoEn: ahora.toISOString(),
    });
    return despues;
  });
}

/** ±15 s o saltar (Req. 12.2). ESCRIBE. */
export async function cambiarDescanso(workoutId: string, cambio: number | 'saltar', ahora: Date = new Date()): Promise<void> {
  await enEscritura([db.workouts], async () => {
    const w = await db.workouts.get(workoutId);
    if (w === undefined || w.descanso === null) return;
    await db.workouts.update(workoutId, {
      descanso: cambio === 'saltar' ? null : ajustarDescanso(w.descanso, cambio, ahora),
      actualizadoEn: ahora.toISOString(),
    });
  });
}

/**
 * Otra serie al final, con los valores de la última (por lado, los de cada
 * lado). En un entrenamiento terminado (historial, Req. 13.3) queda hecha:
 * todo lo de un terminado se hizo, y así cuenta en el resumen y en la vez
 * pasada. ESCRIBE.
 */
export async function agregarSerie(workoutExerciseId: string): Promise<void> {
  await enEscritura([db.workoutSets, db.workoutExercises, db.workouts], async () => {
    const we = await db.workoutExercises.get(workoutExerciseId);
    if (we === undefined) return;
    const workout = await db.workouts.get(we.workoutId);
    const series = (await db.workoutSets.where('workoutExerciseId').equals(workoutExerciseId).toArray()).sort(porOrden);
    const ultima = series[series.length - 1];
    const nueva = serieNueva(we.workoutId, we, (ultima?.orden ?? -1) + 1, {
      tipoSerie: 'normal',
      pesoCapturado: ultima?.pesoCapturado ?? null,
      reps: ultima?.reps ?? null,
      repsIzq: ultima?.repsIzq ?? null,
      repsDer: ultima?.repsDer ?? null,
      duracionSeg: ultima?.duracionSeg ?? null,
      distanciaM: ultima?.distanciaM ?? null,
    });
    const terminado = workout?.estado === 'terminado';
    await db.workoutSets.add(terminado ? { ...nueva, hecha: true, hechaEn: workout.fin ?? ahoraIso() } : nueva);
    await tocar(we.workoutId);
  });
}

/**
 * Quita una serie. En un entrenamiento terminado, si era la última de su
 * ejercicio, también quita el ejercicio, como al terminar. ESCRIBE.
 */
export async function quitarSerie(id: string): Promise<void> {
  await enEscritura([db.workoutSets, db.workoutExercises, db.workouts], async () => {
    const serie = await db.workoutSets.get(id);
    if (serie === undefined) return;
    await db.workoutSets.delete(id);
    const workout = await db.workouts.get(serie.workoutId);
    if (workout?.estado === 'terminado' && (await db.workoutSets.where('workoutExerciseId').equals(serie.workoutExerciseId).count()) === 0) {
      await db.workoutExercises.delete(serie.workoutExerciseId);
    }
    await tocar(serie.workoutId);
  });
}

/**
 * Sube o baja un lugar un ejercicio del entrenamiento (Req. 11.6). Una
 * biserie se mueve completa, para que sus ejercicios sigan juntos y su
 * descanso no cambie. ESCRIBE.
 */
export async function moverEjercicioEnEntrenamiento(workoutExerciseId: string, direccion: -1 | 1): Promise<void> {
  await enEscritura([db.workoutExercises, db.workouts], async () => {
    const we = await db.workoutExercises.get(workoutExerciseId);
    if (we === undefined) return;
    const items = (await db.workoutExercises.where('workoutId').equals(we.workoutId).toArray()).sort(porOrden);
    const bloques = agruparPorBiserie(items, (x) => x.biserie);
    const i = bloques.findIndex((b) => b.some((x) => x.id === workoutExerciseId));
    const actual = bloques[i];
    const otro = bloques[i + direccion];
    if (actual === undefined || otro === undefined) return;
    bloques[i] = otro;
    bloques[i + direccion] = actual;
    for (const [orden, x] of bloques.flat().entries()) {
      if (x.orden !== orden) await db.workoutExercises.update(x.id, { orden });
    }
    await tocar(we.workoutId);
  });
}

/**
 * Cambia la unidad de carga del ejercicio (la app la recuerda, Req. 9.5) y
 * pasa a esa unidad las series que faltan: kg y lb se convierten; con placas
 * la carga queda vacía para escribirla. Las hechas no cambian. ESCRIBE.
 */
export async function cambiarUnidadEnEntrenamiento(workoutExerciseId: string, unidad: UnidadCarga): Promise<void> {
  await enEscritura([db.workoutExercises, db.workoutSets, db.exercises, db.workouts], async () => {
    const we = await db.workoutExercises.get(workoutExerciseId);
    if (we === undefined) return;
    await db.exercises.update(we.exerciseId, { unidadCaptura: unidad, actualizadoEn: ahoraIso() });
    const pendientes = (await db.workoutSets.where('workoutExerciseId').equals(workoutExerciseId).toArray()).filter((s) => !s.hecha);
    for (const s of pendientes) {
      const carga = cargaEnUnidad(s.pesoCapturado, unidad);
      await db.workoutSets.update(s.id, { pesoCapturado: carga, pesoKg: carga === null ? null : capturarCarga(carga.valor, carga.unidad).pesoKg });
    }
    await tocar(we.workoutId);
  });
}

/** Hacer la biserie por separado en este entrenamiento: cada uno con su descanso. ESCRIBE. */
export async function separarBiserie(workoutId: string, clave: string): Promise<void> {
  await enEscritura([db.workoutExercises, db.workouts], async () => {
    await db.workoutExercises
      .where('workoutId')
      .equals(workoutId)
      .filter((x) => x.biserie === clave)
      .modify({ biserie: null });
    await tocar(workoutId);
  });
}

/**
 * Con qué configuración entra un ejercicio que se agrega a mitad del
 * entrenamiento (0.3.8): la de su lugar en la rutina de este entrenamiento,
 * o a mano, diciendo si se anota por lado.
 */
export type EleccionAlAgregar = { tipo: 'rutina'; routineExerciseId: string } | { tipo: 'manual'; porLado: boolean };

export interface OpcionesParaAgregar {
  ejercicio: Exercise;
  /** Cada lugar que el ejercicio tiene en la rutina de este entrenamiento, en orden. */
  deLaRutina: RoutineExercise[];
  /** La vez pasada, en esta modalidad, se anotó por lado. */
  porLadoLaVezPasada: boolean;
  /** Cuántas veces ya está en este entrenamiento. */
  yaEsta: number;
  /**
   * Los que ya están en este entrenamiento, en orden, con su biserie (0.3.9):
   * para decir si una biserie de la rutina se conserva al agregarlo y con quién.
   */
  enElEntrenamiento: { exerciseId: string; biserie: string | null; nombre: string }[];
  modalidad: Modalidad;
  /**
   * Lo que se propone: la de la rutina si la tiene una sola vez; a mano, como
   * la vez pasada, si no la tiene; null si la tiene varias veces: no se elige
   * una por su cuenta.
   */
  propuesta: EleccionAlAgregar | null;
}

/** LECTURA PURA: lo que hace falta para confirmar un ejercicio que se agrega. */
export async function leerOpcionesParaAgregar(workoutId: string, exerciseId: string): Promise<OpcionesParaAgregar | null> {
  const [workout, ejercicio] = await Promise.all([db.workouts.get(workoutId), db.exercises.get(exerciseId)]);
  if (workout === undefined || ejercicio === undefined) return null;
  const modalidad = modalidadDe(workout);
  const deLaRutina =
    workout.routineId === null
      ? []
      : (await db.routineExercises.where('routineId').equals(workout.routineId).toArray())
          .filter((x) => x.exerciseId === exerciseId)
          .sort(porOrden);
  const anterior = await leerVezPasada(exerciseId, workoutId, modalidad);
  const porLadoLaVezPasada = admitePorLado(ejercicio.tipo) && (anterior?.series.some(anotadaPorLado) ?? false);
  const items = (await db.workoutExercises.where('workoutId').equals(workoutId).toArray()).sort(porOrden);
  const nombres = new Map(
    (await db.exercises.bulkGet([...new Set(items.map((x) => x.exerciseId))])).flatMap((x) => (x === undefined ? [] : [[x.id, x.nombre] as const])),
  );
  const unico = deLaRutina.length === 1 ? deLaRutina[0] : undefined;
  return {
    ejercicio,
    deLaRutina,
    porLadoLaVezPasada,
    yaEsta: items.filter((x) => x.exerciseId === exerciseId).length,
    enElEntrenamiento: items.map((x) => ({ exerciseId: x.exerciseId, biserie: x.biserie ?? null, nombre: nombres.get(x.exerciseId) ?? 'Ejercicio' })),
    modalidad,
    propuesta:
      unico !== undefined
        ? { tipo: 'rutina', routineExerciseId: unico.id }
        : deLaRutina.length === 0
          ? { tipo: 'manual', porLado: porLadoLaVezPasada }
          : null,
  };
}

/** Deja `nuevo` en la posición indicada y numera `orden` de 0 en adelante. */
async function ponerEnPosicion(items: readonly WorkoutExercise[], nuevo: WorkoutExercise, posicion: number): Promise<void> {
  const ordenados = [...items.slice(0, posicion), nuevo, ...items.slice(posicion)];
  for (const [orden, x] of ordenados.entries()) {
    if (x.id === nuevo.id) await db.workoutExercises.add({ ...x, orden });
    else if (x.orden !== orden) await db.workoutExercises.update(x.id, { orden });
  }
}

/**
 * Agrega un ejercicio a mitad del entrenamiento (Req. 11.6) con la
 * configuración elegida (0.3.8): la de la rutina conserva objetivo, series,
 * descanso, indicaciones, captura por lado y, si su pareja de biserie sigue
 * aquí, la biserie (queda junto a ella). A mano: sin objetivo, con el
 * descanso predeterminado y por lado solo si se eligió. La vez pasada, en la
 * misma modalidad, prellena las series. Sin elección, se usa la propuesta;
 * si la rutina lo tiene varias veces, hay que elegir. ESCRIBE.
 */
export async function agregarEjercicioAEntrenamiento(
  workoutId: string,
  exerciseId: string,
  eleccion?: EleccionAlAgregar,
): Promise<void> {
  const ajustes = await leerSettings();
  const workout = await db.workouts.get(workoutId);
  if (workout === undefined) throw new ErrorValidacion('Ese entrenamiento ya no existe.');
  if (workout.estado === 'terminado') throw new ErrorValidacion('Ese entrenamiento ya terminó: solo se corrigen sus series.');
  const opciones = await leerOpcionesParaAgregar(workoutId, exerciseId);
  if (opciones === null) throw new ErrorValidacion('Ese ejercicio no existe.');
  const elegida = eleccion ?? opciones.propuesta;
  if (elegida === null) {
    throw new ErrorValidacion('Tu rutina tiene este ejercicio más de una vez: elige con cuál configuración agregarlo.');
  }
  const { ejercicio, modalidad } = opciones;
  const anterior = await leerVezPasada(exerciseId, workoutId, modalidad);
  let config: ReturnType<typeof configuracionDeRutina>;
  let objetivoSeries: number | null = null;
  let plan: PausaYVariante | null = null;
  if (elegida.tipo === 'rutina') {
    const item = opciones.deLaRutina.find((x) => x.id === elegida.routineExerciseId);
    if (item === undefined) throw new ErrorValidacion('Esa configuración ya no está en la rutina: vuelve a elegir.');
    config = configuracionDeRutina(item);
    objetivoSeries = item.series;
    plan = item;
  } else {
    config = {
      repsMin: null,
      repsMax: null,
      descansoSeg: ajustes.descansoPredeterminadoSeg,
      notas: null,
      biserie: null,
      descansoBiserieSeg: null,
      ...(elegida.porLado && admitePorLado(ejercicio.tipo) ? { porLado: true as const } : {}),
    };
  }
  const porLado = config.porLado === true;
  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    const actual = await db.workouts.get(workoutId);
    if (actual === undefined || actual.estado !== 'en_curso') throw new ErrorValidacion('Ese entrenamiento ya no está abierto.');
    const items = (await db.workoutExercises.where('workoutId').equals(workoutId).toArray()).sort(porOrden);
    // La biserie solo se recupera si su pareja sigue aquí y él no está ya en ella
    // (0.3.9: si no, la copia formaba una biserie de tres); entonces va junto a ella
    const { posicion, biserie } = lugarAlAgregar(items, exerciseId, config.biserie);
    const conBiserie = biserie !== null ? config : { ...config, biserie: null, descansoBiserieSeg: null };
    const we: WorkoutExercise = {
      id: nuevoId('ene'),
      workoutId,
      exerciseId,
      orden: posicion,
      ...conBiserie,
      ...(modalidad === 'casa' ? pausaYVarianteIniciales(anterior, plan) : {}),
    };
    await ponerEnPosicion(items, we, posicion);
    const borradores = prellenarSeries(objetivoSeries, anterior, unidadDe(ejercicio, ajustes.unidadPeso), porLado);
    await db.workoutSets.bulkAdd(borradores.map((b, orden) => serieNueva(workoutId, we, orden, b)));
    await tocar(workoutId);
  });
}

/**
 * Deshace "Quitar ejercicio" (0.3.8): vuelve el mismo registro, con su
 * configuración y todas sus series tal como estaban (hechas o no, con sus
 * valores). Solo en un entrenamiento abierto. ESCRIBE.
 *
 * Desde la 0.3.9 vuelve entre sus vecinos de cuando se quitó (`vecinos`),
 * aunque después se haya cambiado el orden, sin partir una biserie, y con su
 * pareja de biserie si sigue aquí (`lugarAlRestaurar`). Sin `vecinos`, por su
 * número de orden.
 */
export async function restaurarEjercicioEnEntrenamiento(
  we: WorkoutExercise,
  series: readonly WorkoutSet[],
  vecinos: VecinosEnSesion | null = null,
): Promise<void> {
  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    const workout = await db.workouts.get(we.workoutId);
    if (workout === undefined || workout.estado !== 'en_curso') throw new ErrorValidacion('Ese entrenamiento ya no está abierto.');
    if ((await db.workoutExercises.get(we.id)) !== undefined) return;
    const items = (await db.workoutExercises.where('workoutId').equals(we.workoutId).toArray()).sort(porOrden);
    const { posicion, biserie } = lugarAlRestaurar(items, we, vecinos);
    // Si ya hay otro igual en su biserie, vuelve solo (como al "Hacer por separado")
    await ponerEnPosicion(items, biserie === (we.biserie ?? null) ? we : { ...we, biserie }, posicion);
    const suyas = series.filter((s) => s.workoutExerciseId === we.id);
    if (suyas.length > 0) await db.workoutSets.bulkPut(suyas);
    await tocar(we.workoutId);
  });
}

/**
 * Deshace "Borrar serie" (0.3.8): vuelve la misma serie, con sus valores, a
 * su lugar entre las de su ejercicio. ESCRIBE.
 */
export async function restaurarSerie(serie: WorkoutSet): Promise<void> {
  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    if ((await db.workoutSets.get(serie.id)) !== undefined) return;
    if ((await db.workoutExercises.get(serie.workoutExerciseId)) === undefined) {
      throw new ErrorValidacion('Ese ejercicio ya no está en el entrenamiento.');
    }
    const otras = (await db.workoutSets.where('workoutExerciseId').equals(serie.workoutExerciseId).toArray()).sort(porOrden);
    const posicion = otras.filter((x) => x.orden < serie.orden).length;
    const ordenadas = [...otras.slice(0, posicion), serie, ...otras.slice(posicion)];
    for (const [orden, x] of ordenadas.entries()) {
      if (x.id === serie.id) await db.workoutSets.add({ ...x, orden });
      else if (x.orden !== orden) await db.workoutSets.update(x.id, { orden });
    }
    await tocar(serie.workoutId);
  });
}

/**
 * La pausa en cada repetición y la variante con que se hace el ejercicio en
 * este entrenamiento (sobre todo en casa). Lo que no se indica no cambia.
 * ESCRIBE.
 */
export async function guardarPausaYVariante(workoutExerciseId: string, cambios: PausaYVariante): Promise<void> {
  // Se valida antes de abrir la transacción (Req. 1.4)
  const limpio = limpiarPausaYVariante(cambios);
  const actualizar: Partial<Pick<WorkoutExercise, 'pausaSeg' | 'variante'>> = {};
  if (cambios.pausaSeg !== undefined) actualizar.pausaSeg = limpio.pausaSeg;
  if (cambios.variante !== undefined) actualizar.variante = limpio.variante;
  await enEscritura([db.workoutExercises, db.workouts], async () => {
    const we = await db.workoutExercises.get(workoutExerciseId);
    if (we === undefined) return;
    await db.workoutExercises.update(workoutExerciseId, actualizar);
    await tocar(we.workoutId);
  });
}

/** Quita un ejercicio y sus series de este entrenamiento. ESCRIBE. */
export async function quitarEjercicioDeEntrenamiento(workoutExerciseId: string): Promise<void> {
  await enEscritura([db.workoutExercises, db.workoutSets, db.workouts], async () => {
    const we = await db.workoutExercises.get(workoutExerciseId);
    if (we === undefined) return;
    await db.workoutSets.where('workoutExerciseId').equals(workoutExerciseId).delete();
    await db.workoutExercises.delete(workoutExerciseId);
    await tocar(we.workoutId);
  });
}

/** ESCRIBE. */
export async function guardarNotasEntrenamiento(workoutId: string, notas: string): Promise<void> {
  const limpio = notas.trim();
  if (limpio.length > 500) throw new ErrorValidacion('Las notas son demasiado largas.');
  await enEscritura([db.workouts], async () => {
    await db.workouts.update(workoutId, { notas: limpio === '' ? null : limpio, actualizadoEn: ahoraIso() });
  });
}

/** Series sin marcar, para preguntar antes de terminar (Req. 11.9). LECTURA PURA. */
export async function seriesSinMarcar(workoutId: string): Promise<number> {
  return db.workoutSets.where('workoutId').equals(workoutId).filter((s) => !s.hecha).count();
}

/**
 * Termina: las series sin marcar se descartan (quien llama ya preguntó) y los
 * ejercicios que quedaron sin series también. ESCRIBE.
 */
export async function terminarEntrenamiento(workoutId: string, ahora: Date = new Date()): Promise<void> {
  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    const w = await db.workouts.get(workoutId);
    if (w === undefined || w.estado !== 'en_curso') return;
    await db.workoutSets.where('workoutId').equals(workoutId).filter((s) => !s.hecha).delete();
    const conSeries = new Set((await db.workoutSets.where('workoutId').equals(workoutId).toArray()).map((s) => s.workoutExerciseId));
    await db.workoutExercises
      .where('workoutId')
      .equals(workoutId)
      .filter((x) => !conSeries.has(x.id))
      .delete();
    await db.workouts.update(workoutId, { estado: 'terminado', fin: ahora.toISOString(), descanso: null, actualizadoEn: ahora.toISOString() });
  });
}

// ---------------------------------------------------------------------------
// Historial (Req. 13)
// ---------------------------------------------------------------------------

export interface EntrenamientoEnHistorial {
  workout: Workout;
  resumen: ResumenEntrenamiento;
}

/**
 * LECTURA PURA: los entrenamientos terminados de un periodo (por su fecha),
 * del más reciente al más antiguo, con duración, series y volumen (Req. 13.1).
 * El volumen no se guarda: sale de las series, así que corregir una serie lo
 * cambia.
 */
export async function leerHistorial(desde: FechaLocal, hasta: FechaLocal): Promise<EntrenamientoEnHistorial[]> {
  const workouts = (await db.workouts.where('fecha').between(desde, hasta, true, true).toArray()).filter(
    (w) => w.estado === 'terminado',
  );
  const series = workouts.length === 0 ? [] : await db.workoutSets.where('workoutId').anyOf(workouts.map((w) => w.id)).toArray();
  return workouts
    .map((workout) => ({ workout, resumen: resumenEntrenamiento(workout, series.filter((s) => s.workoutId === workout.id)) }))
    .sort((a, b) => b.workout.inicio.localeCompare(a.workout.inicio));
}

export interface SesionDeEjercicio {
  workout: Workout;
  /** Las series hechas de ese ejercicio en ese entrenamiento, en orden. */
  series: WorkoutSet[];
}

/** LECTURA PURA: cada sesión terminada con un ejercicio, la más reciente primero (Req. 13.5). */
export async function leerHistorialDeEjercicio(exerciseId: string): Promise<SesionDeEjercicio[]> {
  const series = (await db.workoutSets.where('exerciseId').equals(exerciseId).toArray()).filter((s) => s.hecha);
  const workouts = (await db.workouts.bulkGet([...new Set(series.map((s) => s.workoutId))])).filter(
    (w): w is Workout => w !== undefined && w.estado === 'terminado',
  );
  return workouts
    .map((workout) => ({ workout, series: series.filter((s) => s.workoutId === workout.id).sort(porOrden) }))
    .sort((a, b) => b.workout.inicio.localeCompare(a.workout.inicio));
}

/**
 * Cambia la fecha de un entrenamiento terminado (Req. 13.3): el inicio, el fin
 * y la hora de cada serie se mueven los mismos días, a la misma hora local,
 * así que la duración no cambia y la vez pasada sigue el orden real. No puede
 * quedar en el futuro. ESCRIBE.
 */
export async function cambiarFechaDeEntrenamiento(
  workoutId: string,
  fecha: FechaLocal,
  hoy: FechaLocal,
  ahora: Date = new Date(),
): Promise<void> {
  if (!esFechaLocal(fecha)) throw new ErrorValidacion('La fecha no es válida.', 'fecha');
  if (fecha > hoy) throw new ErrorValidacion('Un entrenamiento terminado no puede quedar en el futuro.', 'fecha');
  await enEscritura([db.workouts, db.workoutSets], async () => {
    const w = await db.workouts.get(workoutId);
    if (w === undefined) throw new ErrorValidacion('Ese entrenamiento ya no existe.');
    if (w.estado !== 'terminado') throw new ErrorValidacion('El entrenamiento en curso se cambia al terminarlo.');
    const dias = diferenciaDias(w.fecha, fecha);
    if (dias === 0) return;
    const inicio = moverDias(w.inicio, dias);
    const fin = w.fin === null ? null : moverDias(w.fin, dias);
    // A la misma hora, hoy podría quedar más tarde que ahora: también sería futuro
    if (Date.parse(fin ?? inicio) > ahora.getTime()) {
      throw new ErrorValidacion('A esa hora, ese día todavía no termina: el entrenamiento quedaría en el futuro.', 'fecha');
    }
    const series = await db.workoutSets.where('workoutId').equals(workoutId).toArray();
    for (const s of series) {
      if (s.hechaEn !== null) await db.workoutSets.update(s.id, { hechaEn: moverDias(s.hechaEn, dias) });
    }
    await db.workouts.update(workoutId, { fecha, inicio, fin, actualizadoEn: ahoraIso() });
  });
}

/** Borra un entrenamiento terminado con sus ejercicios y series, en una transacción (design.md, 7.9). ESCRIBE. */
export async function borrarEntrenamiento(workoutId: string): Promise<void> {
  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    const w = await db.workouts.get(workoutId);
    if (w === undefined) return;
    if (w.estado !== 'terminado') throw new ErrorValidacion('El entrenamiento en curso se descarta desde el entrenamiento.');
    await db.workoutSets.where('workoutId').equals(workoutId).delete();
    await db.workoutExercises.where('workoutId').equals(workoutId).delete();
    await db.workouts.delete(workoutId);
  });
}

/** Descarta el entrenamiento en curso con todo lo que tenía (Req. 11.11). ESCRIBE. */
export async function descartarEntrenamiento(workoutId: string): Promise<void> {
  await enEscritura([db.workouts, db.workoutExercises, db.workoutSets], async () => {
    const w = await db.workouts.get(workoutId);
    if (w === undefined || w.estado !== 'en_curso') return;
    await db.workoutSets.where('workoutId').equals(workoutId).delete();
    await db.workoutExercises.where('workoutId').equals(workoutId).delete();
    await db.workouts.delete(workoutId);
  });
}
